export type StringAITheme = 'light' | 'dark';
export type StringAILocale = 'zh' | 'zh-Hant' | 'en' | 'hi-IN';
export type StringAIPreferenceSource = 'landing' | 'sub2api' | 'openwebui';

export interface StringAIPreferences {
	theme?: StringAITheme;
	locale?: StringAILocale;
	updatedAt?: number;
}

export interface StringAIPreferenceEvent extends StringAIPreferences {
	source: StringAIPreferenceSource;
	updatedAt: number;
}

const THEME_COOKIE = 'string_ai_theme';
const LOCALE_COOKIE = 'string_ai_locale';
const EVENT_KEY = 'string_ai_preferences_event';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

function canUseBrowserStorage() {
	return typeof window !== 'undefined' && typeof document !== 'undefined';
}

export function isStringAITheme(value: unknown): value is StringAITheme {
	return value === 'light' || value === 'dark';
}

export function isStringAILocale(value: unknown): value is StringAILocale {
	return value === 'zh' || value === 'zh-Hant' || value === 'en' || value === 'hi-IN';
}

function readCookie(name: string) {
	if (!canUseBrowserStorage()) return undefined;
	const prefix = `${name}=`;
	const entry = document.cookie
		.split(';')
		.map((part) => part.trim())
		.find((part) => part.startsWith(prefix));
	if (!entry) return undefined;
	try {
		return decodeURIComponent(entry.slice(prefix.length));
	} catch {
		return undefined;
	}
}

function writeCookie(name: string, value: string) {
	if (!canUseBrowserStorage()) return;
	document.cookie = [
		`${name}=${encodeURIComponent(value)}`,
		'Path=/',
		`Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
		'Secure',
		'SameSite=Lax'
	].join('; ');
}

export function readStringAIPreferences(): StringAIPreferences {
	const theme = readCookie(THEME_COOKIE);
	const locale = readCookie(LOCALE_COOKIE);

	return {
		...(isStringAITheme(theme) ? { theme } : {}),
		...(isStringAILocale(locale) ? { locale } : {})
	};
}

export function writeStringAIPreferences(
	preferences: StringAIPreferences,
	source: StringAIPreferenceSource,
	options: { broadcast?: boolean; updatedAt?: number } = {}
): StringAIPreferenceEvent | null {
	if (!canUseBrowserStorage()) return null;

	const current = readStringAIPreferences();
	const next: StringAIPreferences = {
		...current,
		...(isStringAITheme(preferences.theme) ? { theme: preferences.theme } : {}),
		...(isStringAILocale(preferences.locale) ? { locale: preferences.locale } : {})
	};

	if (next.theme) writeCookie(THEME_COOKIE, next.theme);
	if (next.locale) writeCookie(LOCALE_COOKIE, next.locale);

	const event: StringAIPreferenceEvent = {
		...next,
		source,
		updatedAt: options.updatedAt ?? Date.now()
	};

	if (options.broadcast !== false) {
		localStorage.setItem(EVENT_KEY, JSON.stringify(event));
	}

	return event;
}

export function subscribeStringAIPreferences(
	source: StringAIPreferenceSource,
	handler: (event: StringAIPreferenceEvent) => void
): () => void {
	if (!canUseBrowserStorage()) return () => {};

	let lastAppliedAt = 0;
	const listener = (event: StorageEvent) => {
		if (event.key !== EVENT_KEY || !event.newValue) return;

		let payload: StringAIPreferenceEvent;
		try {
			payload = JSON.parse(event.newValue);
		} catch {
			return;
		}

		if (payload.source === source || typeof payload.updatedAt !== 'number') return;
		if (payload.theme !== undefined && !isStringAITheme(payload.theme)) return;
		if (payload.locale !== undefined && !isStringAILocale(payload.locale)) return;
		if (payload.updatedAt <= lastAppliedAt) return;

		lastAppliedAt = payload.updatedAt;
		handler(payload);
	};

	window.addEventListener('storage', listener);
	return () => window.removeEventListener('storage', listener);
}

export function mapStringAILocaleToOpenWebUI(locale: StringAILocale) {
	if (locale === 'hi-IN') return 'hi-IN';
	if (locale === 'zh-Hant') return 'zh-TW';
	if (locale === 'zh') return 'zh-CN';
	return 'en-US';
}

export function mapOpenWebUILocaleToStringAI(locale: string): StringAILocale | undefined {
	if (locale === 'hi' || locale === 'hi-IN') return 'hi-IN';
	if (locale === 'zh-TW' || locale === 'zh-HK') return 'zh-Hant';
	if (locale === 'zh-CN' || locale === 'zh') return 'zh';
	if (locale.startsWith('en')) return 'en';
	return undefined;
}

export function mapOpenWebUILocaleToDayjsLocale(locale: string) {
	return locale === 'hi' || locale === 'hi-IN' ? 'hi' : locale;
}
